/**
 * THE ACTIVE-FILTER CHIPS — what is applied, and how to take one off.
 *
 * A narrowed screen looks exactly like a quiet week. That is the failure mode
 * behind more than one bug report on the screens this replaces: a feed showing
 * three rows because a filter was set reads identically to a feed showing three
 * rows because three things happened. The chip row is the fix, and it only works
 * if EVERY narrowing produces a chip — so this derives them from the filter
 * object rather than from whatever each control remembered to register.
 *
 * A chip carries its own undo as a PATCH, not as a field name. That is what lets
 * one chip clear two fields (a drill selection set both a building and a kind)
 * and what keeps the removal identical to the action that set it.
 */
import {
  BOOK_LABELS,
  DashboardFilter,
  DEFAULT_FILTER,
  FilterMeta,
} from './filter-model';
import { formatDayKey } from '../../../../../function-utils/building-time';

/* A custom period is two day keys; they are printed as dates, never re-zoned. */
const DAY_FULL: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

export interface FilterChip {
  /** Stable across renders, so React keys and E2E selectors do not move. */
  id: string;
  /** The control group it came from — used to order and group the row. */
  group: 'scope' | 'time' | 'classification' | 'people' | 'book' | 'text' | 'chart';
  /** What the chip reads, e.g. "Building". */
  label: string;
  /** The value, in the words the control that set it used. */
  value: string;
  /** Applied to remove exactly this narrowing and nothing else. */
  patch: Partial<DashboardFilter>;
}

const buildingName = (id: number, meta?: FilterMeta): string =>
  meta?.buildings.find(b => b.id === id)?.name || `Building ${id}`;

const portfolioName = (id: number, meta?: FilterMeta): string =>
  meta?.portfolios.find(p => p.id === id)?.name || `Portfolio ${id}`;

const measureLabel = (key: string, meta?: FilterMeta): string =>
  meta?.measures.find(m => m.key === key)?.label || key;

/** `follow_up` -> `Follow up`. The same derivation the panels use, so a chip and
 *  a panel title cannot spell the same value differently. */
const humanise = (key: string): string => {
  const words = String(key || '').replace(/_/g, ' ').trim();

  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};

/**
 * Every applied narrowing, as chips.
 *
 * NOT included, deliberately: the range, the comparison, the granularity, the
 * measure and the book. Each of those is a chrome control that is permanently
 * visible with its current value on it, so a chip would be the same fact twice —
 * and a chip row that restates the controls above it stops being scannable,
 * which is the one job it has.
 *
 * The exception is `measure` inside a DRILL chip, where the measure is part of
 * describing what was clicked.
 */
export const chipsFor = (
  filter: DashboardFilter,
  meta?: FilterMeta
): FilterChip[] => {
  const chips: FilterChip[] = [];
  const d = DEFAULT_FILTER;

  const push = (
    id: string,
    group: FilterChip['group'],
    label: string,
    value: string,
    patch: Partial<DashboardFilter>
  ) => {
    chips.push({ id, group, label, value, patch });
  };

  /* ── scope ── */
  if (filter.portfolioId != null) {
    push('portfolio', 'scope', 'Portfolio', portfolioName(filter.portfolioId, meta), {
      portfolioId: null,
    });
  }
  if (filter.buildingId != null) {
    push('building', 'scope', 'Building', buildingName(filter.buildingId, meta), {
      buildingId: null,
      // A unit is meaningless without the building that disambiguates it.
      unit: null,
    });
  }
  if (filter.unit) {
    push('unit', 'scope', 'Unit', filter.unit, { unit: null });
  }

  /* ── time-of-day. The date RANGE is chrome; a time-of-day window is not. ── */
  if (filter.timeFrom || filter.timeTo) {
    const window =
      filter.timeFrom && filter.timeTo
        ? `${filter.timeFrom}–${filter.timeTo}`
        : filter.timeFrom
        ? `from ${filter.timeFrom}`
        : `before ${filter.timeTo}`;
    push('time', 'time', 'Time of day', window, {
      timeFrom: null,
      timeTo: null,
    });
  }

  /* ── classification ── */
  if (filter.kinds.length) {
    push('kinds', 'classification', 'Kind', filter.kinds.join(', '), { kinds: [] });
  }
  if (filter.severity) {
    push('severity', 'classification', 'Severity', humanise(filter.severity), {
      severity: null,
    });
  }
  if (filter.groups.length) {
    push(
      'groups',
      'classification',
      'Group',
      filter.groups.map(humanise).join(', '),
      { groups: [] }
    );
  }
  if (filter.direction) {
    push('direction', 'classification', 'Direction', humanise(filter.direction), {
      direction: null,
    });
  }
  if (filter.status) {
    push(
      'status',
      'classification',
      'Status',
      filter.status === 'open' ? 'Still inside' : 'Departed',
      { status: null }
    );
  }
  if (filter.priority) {
    push('priority', 'classification', 'Priority', filter.priority, {
      priority: null,
    });
  }

  /* ── people ── */
  if (filter.actors.length) {
    push(
      'actors',
      'people',
      'Recorded by',
      `${filter.actors.length} ${filter.actors.length === 1 ? 'person' : 'people'}`,
      { actors: [] }
    );
  }
  if (filter.role) {
    push('role', 'people', 'Role', humanise(filter.role), { role: null });
  }
  if (filter.name) {
    push('name', 'people', 'First name', filter.name, { name: null });
  }
  if (filter.lastName) {
    push('lastName', 'people', 'Last name', filter.lastName, { lastName: null });
  }

  /* ── book-specific ── */
  if (filter.buildingActivity) {
    push('buildingActivity', 'book', 'Activity', filter.buildingActivity, {
      buildingActivity: null,
    });
  }
  if (filter.visitorType) {
    push('visitorType', 'book', 'Visitor type', filter.visitorType, {
      visitorType: null,
      // The details list is derived from the type, so it cannot outlive it.
      visitorDetails: null,
    });
  }
  if (filter.visitorDetails) {
    push('visitorDetails', 'book', 'Visitor details', filter.visitorDetails, {
      visitorDetails: null,
    });
  }
  if (filter.preAuthorized) {
    push('preAuthorized', 'book', 'Pre-authorised', filter.preAuthorized, {
      preAuthorized: null,
    });
  }
  if (filter.onPass) {
    push('onPass', 'book', 'On pass', filter.onPass, { onPass: null });
  }
  if (filter.approval) {
    push('approval', 'book', 'Approval', filter.approval, { approval: null });
  }
  if (filter.parkingValidation) {
    push('parkingValidation', 'book', 'Parking', filter.parkingValidation, {
      parkingValidation: null,
    });
  }
  if (filter.numberOfGuests != null) {
    push('numberOfGuests', 'book', 'Guests', String(filter.numberOfGuests), {
      numberOfGuests: null,
    });
  }
  if (filter.carrier) {
    push('carrier', 'book', 'Carrier', filter.carrier, { carrier: null });
  }
  if (filter.parcelStatus) {
    const words: Record<string, string> = {
      pending: 'On the shelf',
      picked_up: 'Collected',
      returned: 'Returned to sender',
    };
    push(
      'parcelStatus',
      'book',
      'Parcel status',
      words[filter.parcelStatus] || filter.parcelStatus,
      { parcelStatus: null }
    );
  }

  /* ── free text ── */
  if (filter.q && filter.q !== d.q) {
    push('q', 'text', 'Search', filter.q, { q: '' });
  }

  /* ── the chart click ──
     LAST, because it is the most recent thing the reader did and the one they
     are most likely to want back. Its patch clears both the selection and
     whatever field the drill set, which is why a chip carries a patch rather
     than a field name. */
  if (filter.drill) {
    const { dimension, label, source } = filter.drill;
    const undo: Partial<DashboardFilter> = { drill: null };
    switch (dimension) {
      case 'building':
        undo.buildingId = null;
        undo.unit = null;
        break;
      case 'portfolio':
        undo.portfolioId = null;
        break;
      case 'kind':
        undo.kinds = [];
        break;
      case 'severity':
        undo.severity = null;
        break;
      case 'staff':
        undo.actors = [];
        break;
      case 'day':
      case 'week':
        undo.range = DEFAULT_FILTER.range;
        undo.from = null;
        undo.to = null;
        break;
      case 'hour_of_day':
        undo.timeFrom = null;
        undo.timeTo = null;
        break;
      default:
        break;
    }
    push(
      'drill',
      'chart',
      'From a chart',
      `${source ? `${humanise(source)} · ` : ''}${label}`,
      undo
    );
  }

  return chips;
};

/**
 * The one-line summary of what the screen is showing, for the export cover
 * block, the PDF header and the "Ask IQ" question.
 *
 * Includes the chrome controls the chip row leaves out, because a file that
 * outlives the screen it came from has nothing else to say what window and what
 * measure produced it — which is precisely the failure the analytics CSV already
 * guards against by writing its scope sentence into the file.
 */
export const describeFilter = (
  filter: DashboardFilter,
  meta?: FilterMeta
): string[] => {
  const lines: string[] = [];
  lines.push(`Book: ${BOOK_LABELS[filter.book]}`);
  lines.push(`Measure: ${measureLabel(filter.measure, meta)}`);
  lines.push(
    filter.range === 'custom' && filter.from && filter.to
      ? `Period: ${formatDayKey(filter.from, DAY_FULL)} to ${formatDayKey(filter.to, DAY_FULL)}`
      : `Period: ${humanise(filter.range)}`
  );
  lines.push(`Grouped by: ${humanise(filter.granularity)}`);
  lines.push(
    `Compared with: ${
      filter.compare === 'previous_period' ? 'the previous period' : 'nothing'
    }`
  );
  for (const chip of chipsFor(filter, meta)) {
    lines.push(`${chip.label}: ${chip.value}`);
  }

  return lines;
};
